export const TIPOS_INCIDENCIA = {
  DEPOSICION_MASCOTAS: 'Deposición de mascotas',
  VEHICULO_MAL_ESTACIONADO: 'Vehículo mal estacionado',
  OBJETO_INDEBIDO: 'Objeto indebido',
  USO_FUERA_HORARIO: 'Uso de área fuera de horario',
  MASCOTA_SIN_CORREA: 'Mascota sin correa',
  MAL_USO_IMPLEMENTOS: 'Mal uso de implementos',
  FUMAR_AREA_NO_PERMITIDA: 'Fumar en área no permitida',
  ALTO_VOLUMEN: 'Alto volumen en la residencia',
  ELEVADOR_MANTENIMIENTO: 'Elevador mantenimiento',
  FALTA_ILUMINACION: 'Falta de iluminación',
} as const;

type TipoIncidencia = (typeof TIPOS_INCIDENCIA)[keyof typeof TIPOS_INCIDENCIA];

// Ampliar y mejorar patrones para mejor cobertura y agrupación precisa
const patronesIncidencias: { [K in TipoIncidencia]: string[] } = {
  [TIPOS_INCIDENCIA.DEPOSICION_MASCOTAS]: [
    'deposición',
    'excremento',
    'heces',
    'mascota ensució',
    'necesidades',
    'popo',
    'popó',
    'caca',
    'orina',
    'pipí',
    'defecó',
    'defecacion',
    'defecación',
    'suciedad mascota',
    'ensucio mascota',
    'dejo heces',
    'dejo excremento',
    'dejo popo',
    'dejo popó',
  ],
  [TIPOS_INCIDENCIA.VEHICULO_MAL_ESTACIONADO]: [
    'mal estacionado',
    'hidrante',
    'estacionamiento ajeno',
    'estacionamiento promotora',
    'parqueo indebido',
    'auto mal ubicado',
    'vehículo bloqueando',
    'vehiculo bloqueando',
    'vehículo obstruyendo',
    'vehiculo obstruyendo',
    'carro mal parqueado',
    'carro bloqueando',
    'vehículo en lugar prohibido',
    'vehiculo en lugar prohibido',
  ],
  [TIPOS_INCIDENCIA.OBJETO_INDEBIDO]: [
    'objeto indebido',
    'vidrio en as',
    'objeto en área',
    'objeto prohibido',
    'vidrio en área',
    'basura',
    'escombro',
    'mueble abandonado',
    'objeto olvidado',
    'objeto tirado',
    'material de construcción',
    'desecho',
    'residuo',
    'bolsa de basura',
    'caja en área',
    'paquete en área',
  ],
  [TIPOS_INCIDENCIA.USO_FUERA_HORARIO]: [
    'fuera de horario',
    'horario excedido',
    'sobrepasar horario',
    'después de hora',
    'horario no permitido',
    'uso fuera de horario',
    'uso indebido horario',
    'actividad fuera de horario',
    'actividad nocturna',
    'actividad después de hora',
    'presencia fuera de horario',
    'presencia después de hora',
  ],
  [TIPOS_INCIDENCIA.MASCOTA_SIN_CORREA]: [
    'sin correa',
    'mascota suelta',
    'perro suelto',
    'animal sin correa',
    'mascota sin correa',
    'animal suelto',
    'perro sin correa',
    'gato suelto',
    'gato sin correa',
  ],
  [TIPOS_INCIDENCIA.MAL_USO_IMPLEMENTOS]: [
    'mal uso',
    'gimnasio',
    'área de juegos',
    'implementos',
    'equipos',
    'uso inadecuado',
    'daño implemento',
    'rompió equipo',
    'daño equipo',
    'daño implementos',
    'mal uso implementos',
    'mal uso equipo',
    'mal uso equipos',
    'daño en equipo',
    'daño en implementos',
  ],
  [TIPOS_INCIDENCIA.FUMAR_AREA_NO_PERMITIDA]: [
    'fumar',
    'cigarro',
    'cigarrillo',
    'área no permitida para fumar',
    'fumando',
    'fumador',
    'cenicero',
    'olor a cigarro',
    'olor a cigarrillo',
    'fumó en área',
    'fumó en zona prohibida',
    'fumando en área',
    'fumando en zona prohibida',
  ],
  [TIPOS_INCIDENCIA.ALTO_VOLUMEN]: [
    'alto volumen',
    'ruido',
    'bulla',
    'música alta',
    'volumen excesivo',
    'ruido excesivo',
    'parlante',
    'bocina',
    'festejo',
    'fiesta',
    'gritos',
    'escándalo',
    'sonido fuerte',
    'televisor alto',
    'televisión alta',
    'televisor volumen alto',
    'televisión volumen alto',
  ],
  [TIPOS_INCIDENCIA.ELEVADOR_MANTENIMIENTO]: [
    'elevador',
    'ascensor',
    'mantenimiento elevador',
    'falla elevador',
    'ascensor mantenimiento',
    'ascensor descompuesto',
    'elevador descompuesto',
    'elevador fuera de servicio',
    'ascensor fuera de servicio',
    'ascensor detenido',
    'elevador detenido',
    'falla ascensor',
    'error ascensor',
    'error elevador',
  ],
  [TIPOS_INCIDENCIA.FALTA_ILUMINACION]: [
    'falta iluminación',
    'luz',
    'foco',
    'luminaria',
    'sin luz',
    'poca iluminación',
    'área oscura',
    'luz fundida',
    'foco fundido',
    'luminaria dañada',
    'luminaria fundida',
    'lámpara fundida',
    'lámpara dañada',
    'lámpara apagada',
    'luminaria apagada',
    'sin iluminación',
    'oscuridad',
    'zona oscura',
    'pasillo oscuro',
    'calle oscura',
  ],
};

export const categorizarIncidencia = (descripcion: string): TipoIncidencia | null => {
  if (!descripcion) return null;

  const descripcionLower = descripcion.toLowerCase();

  for (const [tipo, patrones] of Object.entries(patronesIncidencias)) {
    if (patrones.some(patron => descripcionLower.includes(patron.toLowerCase()))) {
      return tipo as TipoIncidencia;
    }
  }

  return null;
};

export const esSinNovedad = (incidencia: string | undefined): boolean => {
  if (!incidencia) return true;
  const terminosSinNovedad = [
    'sin novedad',
    'todo en orden',
    'normal',
    'ok',
    'correcto',
    'nada que reportar',
    'sin incidencias',
    'sin observaciones',
  ];
  return terminosSinNovedad.some(termino => incidencia.toLowerCase().includes(termino));
};

// ================= PRUEBAS DE MEJORAS IMPLEMENTADAS ================= //

/**
 * Ejecutar en consola del navegador: import { runAllTests } from './utils/incidenciasUtils'; runAllTests();
 */

import { fetchRegistros } from '../services/api';
import { autoCategorizarLote } from './autoCategorizacion';
import { config } from '../config';

// Declaración de tipos para window
declare global {
  interface Window {
    probarMejoras?: () => Promise<void>;
    probarMejorasRemoto?: () => Promise<void>;
  }
}

// Prueba 1: Validar paginación
export async function testPagination() {
  console.log('🧪 Probando sistema de paginación...');
  
  try {
    const response = await fetch(`${config.api.baseUrl}/data?paginated=true&page=1&limit=10`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    
    const data = await response.json();
    
    if (data.pagination) {
      console.log('✅ Paginación funcionando:', data.pagination);
      console.log(`📊 Registros: ${data.datos.length}/${data.pagination.totalRecords}`);
      return true;
    } else {
      console.log('❌ Paginación no activa');
      return false;
    }
  } catch (error) {
    console.error('❌ Error en paginación:', error);
    return false;
  }
}

// Prueba 2: Validar cache de categorización
export async function testCacheCategorizacion() {
  console.log('🧪 Probando cache de categorización...');
  
  try {
    console.time('Primera llamada');
    const registros1 = await fetchRegistros();
    const categorizados1 = autoCategorizarLote(registros1);
    console.timeEnd('Primera llamada');
    
    console.time('Segunda llamada');
    const categorizados2 = autoCategorizarLote(registros1);
    console.timeEnd('Segunda llamada');
    
    const iguales = JSON.stringify(categorizados1) === JSON.stringify(categorizados2);
    
    if (iguales) {
      console.log('✅ Cache funcionando - Resultados idénticos');
      return true;
    } else {
      console.log('❌ Cache falló - Resultados diferentes');
      return false;
    }
  } catch (error) {
    console.error('❌ Error en cache:', error);
    return false;
  }
}

// Prueba 3: Validar validación de inputs
export async function testValidation() {
  console.log('🧪 Probando validación de inputs...');
  
  try {
    const response = await fetch(`${config.api.baseUrl}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: 'ab', password: '' })
    });
    
    const data = await response.json();
    
    if (response.status === 400 && data.errors) {
      console.log('✅ Validación funcionando:', data.errors);
      return true;
    } else {
      console.log('❌ Validación no activa');
      return false;
    }
  } catch (error) {
    console.error('❌ Error en validación:', error);
    return false;
  }
}

// Ejecutar todas las pruebas
export async function runAllTests() {
  console.log('🚀 Iniciando pruebas de las mejoras implementadas...\n');
  
  const results = {
    pagination: await testPagination(),
    cache: await testCacheCategorizacion(),
    validation: await testValidation()
  };
  
  console.log('\n📋 Resultados:');
  Object.entries(results).forEach(([test, passed]) => {
    console.log(`   ${test}: ${passed ? '✅ PASÓ' : '❌ FALLÓ'}`);
  });
  
  const passedTests = Object.values(results).filter(Boolean).length;
  const totalTests = Object.keys(results).length;
  
  console.log(`\n🎯 Total: ${passedTests}/${totalTests} pruebas exitosas`);
  
  if (passedTests === totalTests) {
    console.log('🎉 ¡Todas las mejoras funcionan correctamente!');
  } else {
    console.log('⚠️  Algunas mejoras necesitan revisión');
  }
  
  return results;
}

// Función global para consola (sin import)
window.probarMejoras = async function() {
  console.log('🧪 Probando diferentes puertos locales...');
  
  // Probar diferentes puertos donde podría estar el backend
  const puertos = [80, 8000, 8080, 3000, 8888];
  let servidorEncontrado = false;
  
  for (const puerto of puertos) {
    try {
      console.log(`🔍 Probando puerto ${puerto}...`);
      const response = await fetch(`http://localhost:${puerto}/api.php/data?paginated=true&page=1&limit=10`, {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Servidor encontrado en puerto ${puerto}`);
        console.log('✅ Paginación:', data.pagination ? 'FUNCIONA ✅' : 'NO FUNCIONA ❌');
        if (data.pagination) {
          console.log('📊 Info:', data.pagination);
        }
        servidorEncontrado = true;
        break;
      }
    } catch(e) {
      console.log(`❌ Puerto ${puerto} no responde`);
    }
  }
  
  if (!servidorEncontrado) {
    console.log('🚨 No se encontró servidor PHP local');
    console.log('💡 Inicia el servidor PHP con: php -S localhost:8000');
    console.log('💡 O inicia Apache/MAMP/XAMPP');
    return;
  }
  
  console.log('🧪 Probando validación...');
  try {
    const response = await fetch('/api.php/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({username: 'ab', password: ''})
    });
    const data = await response.json();
    console.log('✅ Validación:', data.errors ? 'FUNCIONA ✅' : 'NO FUNCIONA ❌');
    if (data.errors) {
      console.log('🛡️ Errores encontrados:', data.errors);
    }
  } catch(e) {
    const errorMsg = e instanceof Error ? e.message : 'Error desconocido';
    console.log('❌ Error en validación:', errorMsg);
    console.log('💡 Asegúrate de tener el backend PHP corriendo localmente');
  }
  
  console.log('🧪 Cache: Navega por la app y revisa mensajes de categorización');
  console.log('🎉 ¡Pruebas completadas! Revisa los mensajes arriba.');
};

// Función alternativa para probar con servidor remoto (si tienes acceso)
window.probarMejorasRemoto = async function() {
  console.log('🌐 Probando con servidor remoto...');
  try {
    await fetch('https://control.seprovinsa.com/api.php/data?paginated=true&page=1&limit=10', {
      method: 'GET',
      mode: 'no-cors', // Evitar errores CORS
      headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
    });
    console.log('✅ Servidor remoto responde (CORS bloquea lectura pero funciona)');
  } catch(e) {
    console.log('ℹ️ El servidor remoto funciona pero CORS bloquea desde localhost');
  }
};
